import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags';
import { ReactQueryStaleTime } from '@openx/constants/react-query';
import type { UseGetContextualPricing } from '@openx/types/targeting/targetingHookTypes';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { GetPackageContextualPricingDocument } from '../../../graphql/query/packages/GetPackageContextualPricing.generated';
import { GraphQLRequestError } from '../../../modules/graphql/graphQLRequest';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';
import { mapContextualPricing, toContextualSegmentInput, withPartnerNames } from './mapContextualPricing';
import { useFetchSegments } from './useFetchSegments';
import { useRelatedAccountsFetch } from './useRelatedAccountsFetch';

const handleErrorLocally = () => true;

// No filter, so all the accounts the user can see. `useContextualAccount` of the contextual segments block makes the
// same query, so the accounts are read from the Apollo cache rather than fetched again.
const ALL_ACCOUNTS: never[] = [];

// searchOptionsByPath caps `limit` at 500
const MAX_SEGMENTS_LIMIT = 500;

/**
 * Contextual fees for the given segments (preview of POST /package/contextual_pricing). Only for the contextual
 * segments V3 UI, and only with an account and an included segment; otherwise `data` is null and nothing is requested.
 */
export const useGetContextualPricing: UseGetContextualPricing = ({ segments, accountUid }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isContextualSegmentsV3 = useStandaloneContextualSegmentsEnabled();
  useLocalErrorHandling(GetPackageContextualPricingDocument, handleErrorLocally);

  const contextualSegment = useMemo(() => toContextualSegmentInput(segments), [segments]);
  const needsPricing = isContextualSegmentsV3 && !!contextualSegment && !!accountUid;

  // The form knows the account by its uid, the API wants its integer ox3 id.
  const { relatedAccounts, loading: loadingAccounts } = useRelatedAccountsFetch(ALL_ACCOUNTS, {
    enabled: needsPricing,
  });
  // The id comes from an untyped jsonb field and `account_id` is a GraphQL String, so a number is sent as its digits.
  const rawAccountId = accountUid ? relatedAccounts?.[accountUid]?.id : undefined;
  const accountId = rawAccountId == null || rawAccountId === '' ? undefined : String(rawAccountId);
  const isResolvingAccount = needsPricing && loadingAccounts;
  // Without an account id the API would price the fees for the caller's account, so nothing is requested.
  const canPrice = needsPricing && !!accountId;

  // An account that can't be resolved is reported, so that it doesn't look like "no fees".
  const isAccountMissing = needsPricing && !isResolvingAccount && !accountId;
  const accountError = useMemo(
    () => (isAccountMissing ? new Error('Account of the contextual segments not found') : null),
    [isAccountMissing]
  );

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryFn:
      canPrice && contextualSegment
        ? ({ client }) =>
            getGraphQLRequestFromClient(client)(GetPackageContextualPricingDocument, {
              account_id: accountId,
              contextual_segment: contextualSegment,
            })
        : skipToken,
    queryKey: [QueryKeys.CONTEXTUAL_PRICING, accountId, contextualSegment],
    retry: false,
    staleTime: ReactQueryStaleTime.DEFAULT,
  });

  // A skipped query still returns its cached error, so it is used only while pricing.
  const error = accountError ?? (canPrice ? queryError : null);

  useEffect(() => {
    // the global handler already reports a GraphQL error
    if (error && !(error instanceof GraphQLRequestError)) {
      // several components of a form use the hook at once, so one message is enough
      enqueueSnackbar('Failed to fetch contextual pricing', { variant: 'error', preventDuplicate: true });
    }
  }, [error, enqueueSnackbar]);

  // A skipped query still returns its cached data, so it is used only while pricing.
  const pricing = useMemo(
    () => (canPrice ? mapContextualPricing(data?.packageContextualPricing) : null),
    [canPrice, data]
  );

  // The API names the partners by id only. Their names come from the provider names of the included segments, asked
  // for once there are fees. The names are cosmetic, so the fees never wait for them.
  const segmentIds = useMemo(
    () => (pricing ? [...new Set(contextualSegment?.includes?.flatMap(({ val }) => val))] : []),
    [pricing, contextualSegment]
  );
  const includes = contextualSegment?.includes ?? [];
  // The same segment id can exist under each included provider. Past the cap a partner may stay unnamed (shown by id).
  const segmentsLimit = Math.min(Math.max(segmentIds.length * includes.length, 1), MAX_SEGMENTS_LIMIT);
  const { segments: segmentsMetadata } = useFetchSegments({
    account_id: accountId,
    provider_id: includes.map(({ provider }) => provider).join(','),
    limit: segmentsLimit,
    offset: 0,
    segment_id: segmentIds,
  });
  const namedPricing = useMemo(() => withPartnerNames(pricing, segmentsMetadata), [pricing, segmentsMetadata]);

  return { data: namedPricing, loading: isLoading || isResolvingAccount, ...(error ? { error } : {}) };
};
